<template>
  <div>
    <UnifiedFilterBanner mode="salaries" :permalink="props.permalink" :initial-state="props.state"
      :initial-city="props.city" :initial-role="filters.role"
      :pre-fetched-cities="preFetchedCities" :states-cities="statesCities" @filter="handleFilterUpdate" />
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <SectionHeader :title="titleText" class="mb-6" />
      <div class="flex">
        <LastUpdatedDate section="salaries" />
      </div>
      <SalaryCard :key="`salary-card-${salaryCardKey}`" :permalink="props.permalink" :company-name="props.company_name"
        :selected-role="filters.role" :selected-state="filters.state" :selected-city="filters.city"
        :pre-fetched-data="preFetchedSalaryData" :is-ssr-loading="initialSalaryDataLoading"
        :force-server-render="forceServerRender" :description-text="salaryDescription" />
      <SalaryFAQ :company-name="props.company_name" :role="filters.role" :state="filters.state" :city="filters.city"
        :pre-fetched-data="preFetchedFAQData" />
    </div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <div v-if="filters.role && filters.state && filters.city && hasCitiesInStateTableData">
        <SectionHeader
          :title="`${filters.role} Salaries at ${props.company_name} in ${locationDisplay} – City Comparison`"
          :description="`Pay can vary by city depending on local demand and living expenses. Here's how ${filters.role} salaries at ${props.company_name} compare across major cities of ${stateFullName}`"
          class="mb-6" />
        <CitiesInStateTable :permalink="props.permalink" :company-name="props.company_name" :role="filters.role"
          :state="stateFullName" :state-code="filters.state" :pre-fetched-data="preFetchedCitiesStateData" />
        <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink" :sample-size="sampleSize" />
      </div>
      <div v-else-if="filters.role && filters.state && !filters.city && hasRoleSalaryTableData">
        <SectionHeader :title="`${filters.role} Salaries at ${props.company_name} in ${stateFullName}`"
          :description="`See how ${filters.role} salaries at ${props.company_name} vary across ${stateFullName}.`"
          class="mb-6" />
        <RoleSalaryTable :permalink="props.permalink" :company-name="props.company_name" :role="filters.role"
          :initial-data="processedCitiesStateData" />
        <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink" :sample-size="sampleSize" />
      </div>
      <div v-else-if="filters.role && !filters.state && !filters.city">
        <div>
          <SectionHeader :title="`${filters.role} Salaries at ${props.company_name} by location`" :is-html="true"
            margin-bottom="mb-6" />
          <CompanySalaryStateMap :permalink="props.permalink" :company-name="props.company_name" :role="filters.role"
            :initial-data="preFetchedStateMapData"  />
        </div>
        <div v-if="hasRoleSalaryTableData" class="mt-8">
          <SectionHeader :title="`${filters.role} Salaries at ${props.company_name} in Popular Cities`"
            :description="generateLocationSalaryDescriptionText" class="mb-6" />
          <div class="mt-6">
            <RoleSalaryTable :permalink="props.permalink" :company-name="props.company_name" :role="filters.role"
              :initial-data="processedCitiesData" />
            <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink"
              :sample-size="sampleSize" />
          </div>
        </div>
      </div>
      <div v-else-if="!filters.role && filters.state && filters.city && hasSalaryTableData">
        <SectionHeader :title="`${props.company_name} Salaries by Job Title in ${locationDisplay}`"
          :description="`Here's a look at how much different roles at ${props.company_name} pay in ${locationDisplay}:`"
          class="mb-6" />
        <CompanySalaryTable :permalink="props.permalink" :selected-state="filters.state" :selected-city="filters.city"
          :processed-data="processedLocationTableData" :is-loading="locationDataPending" />
        <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink" :sample-size="sampleSize" />
      </div>
      <div v-else-if="!filters.role && filters.state && !filters.city && hasSalaryTableData">
        <SectionHeader :title="`${props.company_name} Salaries by Job Title in ${stateFullName}`"
          :description="`Here's a look at how much different roles at ${props.company_name} pay in ${stateFullName}:`"
          class="mb-6" />
        <CompanySalaryTable :permalink="props.permalink" :selected-state="filters.state"
          :processed-data="processedLocationTableData" :is-loading="locationDataPending" />
        <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink" :sample-size="sampleSize" />
      </div>
      <div v-else>
        <div>
          <SectionHeader :title="`${props.company_name} Average Salaries by Location`"
            :description="`Wondering how pay changes based on where you work? Explore average salaries at ${props.company_name} across the U.S.`"
            margin-bottom="mb-6" class="mt-8" />
          <CompanySalaryStateMap :permalink="props.permalink" :company-name="props.company_name"
            :initial-data="preFetchedCompanyStateMapData"  />
        </div>
        <div v-if="hasSalaryTableData" class="mt-8">
          <SectionHeader :title="`${props.company_name} Salaries by Job Title`"
            :description="generateSalaryDescriptionText" :is-html="true" class="mb-6" />
          <CompanySalaryTable :permalink="props.permalink" :pre-fetched-data="preFetchedDefaultData"
            :processed-data="processedSalaryTableData" :is-loading="defaultDataPending" />
          <SalaryMethodology :company-name="props.company_name" :permalink="props.permalink"
            :sample-size="sampleSize" />
        </div>
      </div>
    </div>
    <div v-if="!filters.state && !filters.city" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <SectionHeader :title="`Latest Job Openings at ${props.company_name}`" class="mb-6">
        <template #action>
          <NuxtLink :to="`/company/${props.permalink}/jobs`" class="hidden sm:block">
            <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" />
          </NuxtLink>
        </template>
      </SectionHeader>
      <div class="block sm:hidden mb-4">
        <NuxtLink :to="`/company/${props.permalink}/jobs`">
          <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
            :brand-color="props.brand_color" class="justify-start" />
        </NuxtLink>
      </div>
      <CompanyTopJobs :permalink="props.permalink" :location="''" :company_name="props.company_name"
        :pre-fetched-data="preFetchedJobsData" :is-loading="jobsDataPending" :logo_url="logoUrl" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import SectionHeader from '../common/SectionHeader.vue'
import ActionButton from '../common/ActionButton.vue'
import CompanySalaryStateMap from './salary/CompanySalaryStateMap.vue'
import CompanySalaryTable from './salary/CompanySalaryTable.vue'
import RoleSalaryTable from './salary/RoleSalaryTable.vue'
import CitiesInStateTable from './salary/CitiesInStateTable.vue'
import SalaryCard from './salary/SalaryCard.vue'
import SalaryFAQ from './salary/SalaryFAQ.vue'
import SalaryMethodology from './salary/SalaryMethodology.vue'
import UnifiedFilterBanner from './filters/UnifiedFilterBanner.vue'
import CompanyTopJobs from './CompanyTopJobs.vue'
import LastUpdatedDate from './LastUpdatedDate.vue'
import {
  generateSalaryDescription,
  generateCompanySalaryDescription,
  generateLocationSalaryDescription
} from '~/utils/salaryDescription'
import { provideGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import { hasDataForComponent } from '~/utils/salaryDataValidator'
const salaryPeriodState = provideGlobalSalaryPeriod()
const { activePeriod, adjustValueForPeriod } = salaryPeriodState
interface Props {
  permalink: string
  company_name: string
  state?: string
  city?: string
  initialRole?: string
  initialData?: any
  initialFilters?: any
  salaryData?: any
  stateSalaryData?: any
  citiesData?: any
  roleSalaryData?: any
  preFetchedData?: any
  logoUrl?: string
  brand_color?: string
}
const props = withDefaults(defineProps<Props>(), {
  state: '',
  city: '',
  initialRole: '',
  initialData: undefined,
  initialFilters: undefined,
  salaryData: undefined,
  stateSalaryData: undefined,
  citiesData: undefined,
  roleSalaryData: undefined,
  preFetchedData: undefined,
  logoUrl: '',
  brand_color: '#5F0CE2'
})
const permalinkRef = ref(props.permalink)
const companyNameRef = ref(props.company_name)
const initialFiltersRef = ref(
  props.initialFilters || {
    role: props.initialRole || '',
    state: props.state || '',
    city: props.city || ''
  }
)
const { filters, stateFullName, locationDisplay, handleFilterUpdate }
  = useFilters(initialFiltersRef, permalinkRef)
const salaryCardKey = ref(0)
const forceServerRender = ref(true)
watch(
  [() => filters.role, () => filters.state, () => filters.city],
  () => {
    salaryCardKey.value++
    if (import.meta.client) {
      forceServerRender.value = false
    }
  },
  { deep: true }
)
const preFetchedFAQData = computed(() => {
  if (props.preFetchedData?.faqData) {
    return props.preFetchedData.faqData.data
  }
  return null
})
const preFetchedSalaryData = computed(() => {
  if (props.preFetchedData?.salaryDetail) {
    return props.preFetchedData.salaryDetail
  }
  return null
})
const initialSalaryDataLoading = computed(() => {
  return !props.preFetchedData?.salaryDetail
})
const preFetchedCitiesStateData = computed(() => {
  return props.preFetchedData?.citiesData || null
})
const preFetchedStateMapData = computed(() => {
  return props.preFetchedData?.stateMapData || null
})
const preFetchedCompanyStateMapData = computed(() => {
  return props.preFetchedData?.stateMapData || null
})
const preFetchedCitiesRoleData = computed(() => {
  return props.preFetchedData?.citiesRoleData || null
})
const preFetchedLocationData = computed(() => {
  return props.preFetchedData?.locationData || null
})
const locationDataPending = computed(() => {
  return !props.preFetchedData?.locationData
})
const preFetchedDefaultData = computed(() => {
  return props.preFetchedData?.defaultData || null
})
const defaultDataPending = computed(() => {
  return !props.preFetchedData?.defaultData
})
const preFetchedCitiesData = computed(() => {
  return props.preFetchedData?.citiesData || null
})
const preFetchedCities = computed(() => {
  if (
    preFetchedCitiesData.value?.cities
    && Array.isArray(preFetchedCitiesData.value.cities)
  ) {
    return preFetchedCitiesData.value.cities
  }
  return []
})
const statesCities = computed(() => {
  if (props.preFetchedData?.statesCities) {
    return props.preFetchedData.statesCities
  }
  return {}
})
const processedSalaryTableData = computed(() => {
  if (!preFetchedDefaultData.value) return []
  if (
    preFetchedDefaultData.value.success
    && 'data' in preFetchedDefaultData.value
    && preFetchedDefaultData.value.data
  ) {
    const rawData = preFetchedDefaultData.value.data
    if (rawData && Array.isArray(rawData.salaries)) {
      const sorted = rawData.salaries
        .filter((item: any) => item && typeof item === 'object' && item.job_title)
        .sort((a: any, b: any) => (b.count || 0) - (a.count || 0))
      const deliveryDriverIndex = sorted.findIndex(
        (item: any) => item.job_title?.toLowerCase() === 'delivery driver'
      )
      if (deliveryDriverIndex > 0) {
        const [deliveryDriver] = sorted.splice(deliveryDriverIndex, 1)
        sorted.unshift(deliveryDriver)
      }
      return sorted
        .slice(0, 10)
        .map((item: any) => ({
          job_title: item.job_title,
          soc_code: item.soc_code || '',
          soc_code_id: item.soc_code_id || '',
          annual_pay: item.adjusted_salary?.P50 || 0,
          hourly_pay: (item.adjusted_salary?.P50 || 0) / 2080,
          count: item.count || 0
        }))
    }
    if (Array.isArray(rawData)) {
      return rawData
    }
  }
  return []
})
const processedCitiesData = computed<CityData[]>(() => {
  if (!preFetchedCitiesRoleData.value) return []
  if (
    preFetchedCitiesRoleData.value.success
    && 'data' in preFetchedCitiesRoleData.value
  ) {
    if (Array.isArray(preFetchedCitiesRoleData.value.data)) {
      return preFetchedCitiesRoleData.value.data as CityData[]
    }
    if (
      preFetchedCitiesRoleData.value.data
      && typeof preFetchedCitiesRoleData.value.data === 'object'
      && 'cities' in preFetchedCitiesRoleData.value.data
    ) {
      return (preFetchedCitiesRoleData.value.data.cities as CityData[]) || []
    }
  }
  return []
})
const processedCitiesStateData = computed<CityData[]>((): CityData[] => {
  if (!preFetchedCitiesStateData.value) return []
  if (
    preFetchedCitiesStateData.value.success
    && 'data' in preFetchedCitiesStateData.value
  ) {
    if (Array.isArray(preFetchedCitiesStateData.value.data)) {
      return preFetchedCitiesStateData.value.data as CityData[]
    }
    if (
      preFetchedCitiesStateData.value.data
      && 'cities' in preFetchedCitiesStateData.value.data
    ) {
      return (preFetchedCitiesStateData.value.data.cities as CityData[]) || []
    }
  }
  return []
})
const processedLocationTableData = computed(() => {
  if (!preFetchedLocationData.value) return []
  if (
    preFetchedLocationData.value.success
    && 'data' in preFetchedLocationData.value
  ) {
    if (Array.isArray(preFetchedLocationData.value.data)) {
      return preFetchedLocationData.value.data
    }
  }
  return []
})
const generateSalaryDescriptionText = computed(() => {
  return generateCompanySalaryDescription(
    processedSalaryTableData.value,
    props.company_name || '',
    adjustValueForPeriod
  )
})
const generateLocationSalaryDescriptionText = computed(() => {
  return generateLocationSalaryDescription(
    processedCitiesData.value,
    props.company_name || '',
    filters.role || '',
    adjustValueForPeriod
  )
})
const salaryDescription = computed(() => {
  return generateSalaryDescription(
    salaryCardData.value,
    props.company_name || '',
    {
      city: filters.city,
      state: filters.state
    },
    filters.role,
    adjustValueForPeriod
  )
})
const salaryCardData = computed(() => {
  if (preFetchedSalaryData.value && 'data' in preFetchedSalaryData.value) {
    return preFetchedSalaryData.value.data
  }
  if (
    preFetchedSalaryData.value
    && 'adjusted_salary' in preFetchedSalaryData.value
  ) {
    return preFetchedSalaryData.value
  }
  return null
})
const { titleText } = useTitleGenerator(
  filters,
  companyNameRef,
  stateFullName,
  locationDisplay
)
const router = useRouter()
watch(activePeriod, () => { })
const sampleSize = computed(() => {
  if (preFetchedSalaryData.value?.data?.count) {
    return preFetchedSalaryData.value.data.count
  }
  if (preFetchedDefaultData.value?.data?.count) {
    return preFetchedDefaultData.value.data.count
  }
  return 500
})
const hasStateMapData = computed(() =>
  hasDataForComponent('CompanySalaryStateMap', props.preFetchedData)
)
const hasSalaryTableData = computed(() =>
  hasDataForComponent('CompanySalaryTable', props.preFetchedData)
)
const hasRoleSalaryTableData = computed(() =>
  hasDataForComponent('RoleSalaryTable', props.preFetchedData)
)
const hasCitiesInStateTableData = computed(() =>
  hasDataForComponent('CitiesInStateTable', props.preFetchedData)
)
const preFetchedJobsData = computed(() => {
  return props.preFetchedData?.jobsData || { jobs: [], total: 0, available: 0 }
})
const jobsDataPending = computed(() => {
  return !props.preFetchedData?.jobsData
})
</script>